/** @jsx jsx */
import { jsx } from '@emotion/core'
import React from 'react'
import { MATCH_STATUS, SPORT_ID } from 'adv-protocol/src/GameCommon'
import _ from 'lodash'
import { TimeLineWrap } from '@styles/match.style'
import { PeriodInfo, PeriodScore, SimpleMatchTimeline } from '@interface/chat'
import { observer } from 'mobx-react'
import { MatchRecordSimpleMatchInfo } from '@views/MatchRecord/index'
import classNames from 'classnames'

export interface Props {
  matchRecordSimpleMatchInfo: MatchRecordSimpleMatchInfo
  simpleMatchTimeline: SimpleMatchTimeline
}

interface ScoreInfo {
  homeScore: number
  awayScore: number
  homeWin: boolean
  awayWin: boolean
}

export const MatchTimeTable: React.FC<Props> = observer((props: Props) => {
  const {
    matchRecordSimpleMatchInfo,
    simpleMatchTimeline = { periodScoreList: [], periodInfoList: [] },
  } = props

  const { periodScoreList, periodInfoList } = simpleMatchTimeline
  const { sportId, homeTeamName, awayTeamName, isLiveMatch } = matchRecordSimpleMatchInfo

  let periodCount = _.isEmpty(periodScoreList) ? _.size(periodInfoList) : _.size(periodScoreList)
  if (isLiveMatch && _.size(periodScoreList) < _.size(periodInfoList)) {
    periodCount = _.size(periodInfoList)
  }

  const addBlankScore
    = (beforePeriodScore: PeriodScore | PeriodInfo, currentPeriodScore: PeriodScore | PeriodInfo): boolean => {
    return (sportId === SPORT_ID.SOCCER || sportId === SPORT_ID.HANDBALL)
      && currentPeriodScore.matchStatusCode === MATCH_STATUS.Penalties
      && !_.isNil(beforePeriodScore)
      && beforePeriodScore.matchStatusCode !== MATCH_STATUS.Overtime    // 연장전이 없는 경기가 있음
      && beforePeriodScore.matchStatusCode !== MATCH_STATUS.FirstExtra  // 연장 전, 후반으로 나뉜 경기 중 전반만 존재하는 경기가 있음
      && beforePeriodScore.matchStatusCode !== MATCH_STATUS.SecondExtra  // 연장 전, 후반으로 나뉜 경기 중 후반까지 존재하는 경기가 있음
  }

  /**
   * betradar match summary 로 스코어정보 생성
   */
  const makeScoreInfoByPeriodScore = (): ScoreInfo[] => {
    const scoreList: ScoreInfo[] = []
    let beforePeriodScore: PeriodScore | PeriodInfo
    for (const periodScore of periodScoreList) {
      if (addBlankScore(beforePeriodScore, periodScore)) {
        scoreList.push({
          homeScore: null,
          awayScore: null,
          homeWin: null,
          awayWin: null,
        })
      }

      scoreList.push({
        homeScore: periodScore.homeScore,
        awayScore: periodScore.awayScore,
        homeWin: periodScore.homeScore > periodScore.awayScore,
        awayWin: periodScore.homeScore < periodScore.awayScore,
      })

      beforePeriodScore = periodScore
    }
    return scoreList
  }

  /**
   * betradar timeline 으로 스코어정보 생성.
   * 기본적으로는 match summary 로 정보를 생성하지만 match summary 가 없을 경우에 대비
   * 또한 라이브 경기는 match summary 가 쿼터나 이닝이 끝나야 생성되므로 timeline 정보로 스코어를 생성한다.
   */
  const makeScoreInfoByPeriodInfo = (): ScoreInfo[] => {
    const scoreList: ScoreInfo[] = []
    let beforePeriodScore: PeriodScore | PeriodInfo
    for (const periodInfo of periodInfoList) {
      if ((sportId === SPORT_ID.SOCCER || sportId === SPORT_ID.HANDBALL)
        && periodInfo.matchStatusCode === MATCH_STATUS.SecondExtra) { // 축구 연장 후반
        scoreList[2].homeScore += periodInfo.homeScore
        scoreList[2].awayScore += periodInfo.awayScore
        scoreList[2].homeWin = scoreList[2].homeScore > scoreList[2].awayScore
        scoreList[2].awayWin = scoreList[2].homeScore < scoreList[2].awayScore
      } else {
        if (addBlankScore(beforePeriodScore, periodInfo)) {
          scoreList.push({
            homeScore: null,
            awayScore: null,
            homeWin: null,
            awayWin: null,
          })
        }
        scoreList.push({
          homeScore: periodInfo.homeScore,
          awayScore: periodInfo.awayScore,
          homeWin: periodInfo.homeScore > periodInfo.awayScore,
          awayWin: periodInfo.homeScore < periodInfo.awayScore,
        })
      }
      beforePeriodScore = periodInfo
    }
    return scoreList
  }

  const makeScoreInfoList = (): ScoreInfo[] => {
    let scoreList: ScoreInfo[] = []
    if (isLiveMatch && _.size(periodScoreList) < _.size(periodInfoList)) {
      scoreList = makeScoreInfoByPeriodInfo()
    } else if (!_.isEmpty(periodScoreList)) {
      scoreList = makeScoreInfoByPeriodScore()
    } else if (!_.isEmpty(periodInfoList)) {
      scoreList = makeScoreInfoByPeriodInfo()
    }
    return scoreList
  }

  const makeMatchTimeTable = (basePeriod: number) => {
    const scoreList = makeScoreInfoList()
    const elems = [
      <tr key="timetable-event-table-row-home">
        <td key="timetable-event-home-item-name" className="team-name">{homeTeamName}</td>
        {_.range(basePeriod).map(v => {
          const homeScore = scoreList[v]?.homeScore ?? '-'
          const scoreClassName = classNames('score', {
            win: scoreList[v]?.homeWin,
          })
          const key = `timeline-event-away-item-${v}`
          return <td key={key} className={scoreClassName}>{homeScore}</td>
        })}
        {(sportId === SPORT_ID.BASEBALL || sportId === SPORT_ID.VOLLEYBALL) && _.size(scoreList) > basePeriod && (
          _.range(basePeriod, _.size(scoreList)).map((v) => {
            const homeScore = scoreList[v]?.homeScore ?? '-'
            const scoreClassName = classNames('score', {
              win: scoreList[v]?.homeWin,
            })
            const key = `timeline-event-away-item-${v}`
            return <td key={key} className={scoreClassName}>{homeScore}</td>
          })
        )}
      </tr>,
      <tr key="timetable-event-table-row-away">
        <td key="timetable-event-away-item-name" className="team-name">{awayTeamName}</td>
        {_.range(basePeriod).map(v => {
          const awayScore = scoreList[v]?.awayScore ?? '-'
          const scoreClassName = classNames('score', {
            win: scoreList[v]?.awayWin,
          })
          const key = `timeline-event-away-item-${v}`
          return <td key={key} className={scoreClassName}>{awayScore}</td>
        })}
        {(sportId === SPORT_ID.BASEBALL || sportId === SPORT_ID.VOLLEYBALL) && _.size(scoreList) > basePeriod && (
          _.range(basePeriod, _.size(scoreList)).map((v) => {
            const awayScore = scoreList[v]?.awayScore ?? '-'
            const scoreClassName = classNames('score', {
              win: scoreList[v]?.awayWin,
            })
            const key = `timeline-event-away-item-${v}`
            return <td key={key} className={scoreClassName}>{awayScore}</td>
          })
        )}
      </tr>,
    ]
    if (sportId === SPORT_ID.BASEBALL) {
      elems.reverse()
    }
    return (
      <tbody className="table--body">
      {elems}
      </tbody>
    )
  }
  const makeTimeTable = () => {
    let contents
    switch (sportId) {
      case SPORT_ID.SOCCER:
      case SPORT_ID.HANDBALL:
        contents = (
          <div className="timeline--table">
            <table className="table">
              <thead className="table--head">
              <tr>
                <th className="screen-out">팀</th>
                <th className="round">전반</th>
                <th className="round">후반</th>
                <th className="round">연장</th>
                <th className="round">승부</th>
              </tr>
              </thead>
              {makeMatchTimeTable(4)}
            </table>
          </div>
        )
        break
      case SPORT_ID.WATERPOLO: // 수구
      case SPORT_ID.BASKETBALL:
        contents = (
          <div className="timeline--table">
            <table className="table">
              <thead className="table--head">
              <tr>
                <th className="screen-out">팀</th>
                <th className="round">1쿼터</th>
                <th className="round">2쿼터</th>
                <th className="round">3쿼터</th>
                <th className="round">4쿼터</th>
                {periodCount > 4 && (
                  <th className="round">연장</th>
                )}
              </tr>
              </thead>
              {makeMatchTimeTable(periodCount)}
            </table>
          </div>
        )
        break
      case SPORT_ID.BASEBALL:
        contents = (
          <div className="timeline--table baseball">
            <table className="table">
              <thead className="table--head">
              <tr>
                <th className="screen-out">팀</th>
                <th className="round">1</th>
                <th className="round">2</th>
                <th className="round">3</th>
                <th className="round">4</th>
                <th className="round">5</th>
                <th className="round">6</th>
                <th className="round">7</th>
                <th className="round">8</th>
                <th className="round">9</th>
                {periodCount > 9 && (
                  _.range(9, periodCount).map((v) => {
                    const key = `timeline-baseball-head-${v}`
                    return <th key={key} className="round overtime">연장</th>
                  })
                )}
              </tr>
              </thead>
              {makeMatchTimeTable(9)}
            </table>
          </div>
        )
        break
      case SPORT_ID.VOLLEYBALL:
        contents = (
          <div className="timeline--table">
            <table className="table">
              <thead className="table--head">
              <tr>
                <th className="screen-out">팀</th>
                <th className="round">1세트</th>
                <th className="round">2세트</th>
                <th className="round">3세트</th>
                <th className="round">4세트</th>
                <th className="round">5세트</th>
                {periodCount > 5 && (
                  _.range(5, periodCount).map((v) => {
                    const key = `timeline-volleyball-head-${v}`
                    return <th key={key} className="round">{v}세트</th>
                  })
                )}
              </tr>
              </thead>
              {makeMatchTimeTable(5)}
            </table>
          </div>
        )
        break
      case SPORT_ID.ICE_HOCKEY:
        contents = (
          <div className="timeline--table">
            <table className="table">
              <thead className="table--head">
              <tr>
                <th className="screen-out">팀</th>
                <th className="round icehockey">1P</th>
                <th className="round icehockey">2P</th>
                <th className="round icehockey">3P</th>
                {periodCount > 3 && (<th className="round icehockey">연장</th>)}
                {periodCount === 5 && (<th className="round icehockey">승부</th>)}
              </tr>
              </thead>
              {makeMatchTimeTable(periodCount)}
            </table>
          </div>
        )
        break
      case SPORT_ID.TENNIS:
        contents = (
          <div className="timeline--table">
            <table className="table">
              <thead className="table--head">
              <tr>
                <th className="screen-out">선수</th>
                <th className="round">1세트</th>
                <th className="round">2세트</th>
                <th className="round">3세트</th>
                <th className="round">4세트</th>
                <th className="round">5세트</th>
              </tr>
              </thead>
              {makeMatchTimeTable(5)}
            </table>
          </div>
        )
        break
      case SPORT_ID.TABLE_TENNIS:
        contents = (
          <div className="timeline--table table-tennis">
            <table className="table">
              <thead className="table--head">
              <tr>
                <th className="screen-out">선수</th>
                <th className="round">1세트</th>
                <th className="round">2세트</th>
                <th className="round">3세트</th>
                <th className="round">4세트</th>
                <th className="round">5세트</th>
                <th className="round">6세트</th>
                <th className="round">7세트</th>
                <th className="round"></th>
              </tr>
              </thead>
              {makeMatchTimeTable(7)}
            </table>
          </div>
        )
        break
      default:
        break
    }
    return contents
  }

  const wrapClassName = classNames('timeline', {
    'timeline-none': _.isEmpty(makeTimeTable()),
  })

  return (
    <TimeLineWrap className={wrapClassName}>
      {makeTimeTable()}
    </TimeLineWrap>
  )
})
